import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataSourceOrdenacaoInsensivel } from '@shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '@shared/components/empty-list/empty-list.component';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { Masks, MaskService } from '@shared/services/mask.service';
import { CnaeService } from '@shared/services/apis/servico/cnae.service';
import { Cnae } from '@shared/services/models/servico/cnae.model';
import { CnaeBulkDeleteRequest } from '@shared/services/requests/servico/cnae-bulk-delete.request';
import { GetCnaeResponse } from '@shared/services/responses/servico/get-cnae.response';
import { ReturnStruct } from '@shared/services/structs/return.struct';
import { HeaderComponent } from '@shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { UtilService } from '@shared/services/util.service';

@Component({
  selector: 'app-cnaes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    NgxMaskPipe,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './cnaes.component.html',
  styleUrl: './cnaes.component.css',
})
export class CnaesComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public masks: Masks;
  public model: FormGroup;
  public _paginaAtual: number = 0;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;
  public _dataSource = new DataSourceOrdenacaoInsensivel<Cnae>();
  public _isLoading: boolean = false;
  public isExcluindoSelecionados: boolean = false;
  public idCnaeExcluindo: number | null = null;
  public displayedColumns: string[] = ['select', 'codigo', 'descricao', 'actions'];
  public selection = new SelectionModel<Cnae>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private cnaeService: CnaeService,
    private alertService: AlertService,
    private maskService: MaskService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.masks = this.maskService.getMasks();
    this.model = this.formBuilder.group({ pesquisa: [''] });
    this.loadFilters();
    this.carregarDados();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/servicos/cnaes/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarDados(reset: boolean = false): void {
    this._isLoading = true;
    const pesquisa = this.model.get('pesquisa')?.value || '';
    this.cnaeService.getAll(pesquisa, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => this.tratarRetornoListagem(response, reset),
      error: () => {
        this._isLoading = false;
        this.alertService.show(
          'Aviso',
          'Não foi possível carregar os CNAEs agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  private tratarRetornoListagem(response: GetCnaeResponse, reset: boolean): void {
    this._isLoading = false;
    if (response.isError) {
      this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
      return;
    }
    this._totalRegistros = response.totalRegistros;
    this._dataSource.data = reset ? response.listaCnaes : [...this._dataSource.data, ...response.listaCnaes];
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarDados();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this.selection.clear();
    this._dataSource.data = [];
    this.carregarDados(true);
  }

  public isAllSelected(): boolean {
    return (
      this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0
    );
  }

  public toggleAllRows(): void {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this._dataSource.data);
  }

  public abrirModalExcluir(item?: Cnae): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];
    if (itemsToDelete.length === 0) return;

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar exclusão',
        description: 'Os CNAEs selecionados serão excluídos. Confirma a exclusão?',
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result?.confirm) return;
      this.excluirCnaes(itemsToDelete, isBulk);
    });
  }

  private excluirCnaes(itens: Cnae[], isBulk: boolean): void {
    if (isBulk) {
      this.isExcluindoSelecionados = true;
    } else {
      this.idCnaeExcluindo = itens[0].idCnae;
    }

    const request: CnaeBulkDeleteRequest = { ids: itens.map(i => i.idCnae) };

    this.cnaeService.deleteBulk(request).subscribe({
      next: r => this.tratarRetornoExclusao(r, itens, isBulk),
      error: () => {
        this.finalizarExclusao();
        this.alertService.show(
          'Aviso',
          isBulk
            ? 'Não foi possível excluir os CNAEs agora. Tente novamente em alguns instantes.'
            : 'Não foi possível excluir o CNAE agora. Tente novamente em alguns instantes.',
          AlertType.warning
        );
      },
    });
  }

  private tratarRetornoExclusao(r: ReturnStruct, itens: Cnae[], isBulk: boolean): void {
    this.finalizarExclusao();

    if (r.isError) {
      this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
      return;
    }

    this.alertService.show(
      'Sucesso',
      isBulk ? 'CNAEs excluídos com sucesso' : 'CNAE excluído com sucesso',
      AlertType.success
    );
    this.selection.deselect(...itens);
    this.clickPesquisar();
  }

  private finalizarExclusao(): void {
    this.isExcluindoSelecionados = false;
    this.idCnaeExcluindo = null;
  }
}
